import type { MapInteractiveProps, MapSizes } from "./map-interactive.type";
import type {
  MapEvent,
  MapMouseEvent,
  MapProps,
} from "@vis.gl/react-google-maps";
import type { CSSProperties } from "react";

import { useMap } from "@vis.gl/react-google-maps";
import { useEffect } from "react";

import { useWindowDimensions } from "@/hooks/useWindowDimensions";
import { useLocalStorageValue } from "@/utilities/local-storage";

import { getMarkersGroupedByPosition } from "../map.helper";

import {
  BASE_MAP_OPTIONS,
  MAP_DEFAULT_CENTER,
  MAP_DEFAULT_ZOOM,
} from "./map-interactive.constant";
import { centerMap, handleMapClick } from "./map-interactive.helper";

export const useMapInteractive = (props: MapInteractiveProps) => {
  const {
    id,
    geojson,
    geojsonOptions,
    noInfoWindow,
    subItemsDepth,
    className,
  } = props;

  const markers = getMarkersGroupedByPosition({
    markers: props.markers,
    subItemsDepth,
  });

  // Use markers already grouped in `useMapProps()` to make sure center,
  // zoom and fit bounds are calculated based on final markers.
  const mapProps = useMapProps({ ...props, markers });

  return {
    // Return calculated vars
    markers,
    mapProps,
    // Return untouched props
    id,
    geojson,
    geojsonOptions,
    noInfoWindow,
    className,
  };
};

export const useMapProps = ({
  id,
  markers,
  onMapClick,
  fitBoundsPadding,
  centerOnMarkersChange,
  onCenterMap,
}: MapInteractiveProps): MapProps => {
  const map = useMap(id);
  const [mapTypeId, setMapTypeId] = useLocalStorageValue("map-type-id");

  const onMapTypeIdChanged = (event: MapEvent) => {
    const newMapTypeId = event.map.getMapTypeId();
    if (newMapTypeId) setMapTypeId(newMapTypeId);
  };

  const onClick = (event: MapMouseEvent) => {
    handleMapClick({ event, onMapClick });
  };

  useEffect(() => {
    if (!map) return;
    centerMap({ map, markers, fitBoundsPadding });
    onCenterMap?.();
  }, [map, id]);

  useEffect(() => {
    if (!map || !centerOnMarkersChange) return;
    centerMap({ map, markers, fitBoundsPadding });
    onCenterMap?.();
  }, [centerOnMarkersChange, markers]);

  return {
    ...BASE_MAP_OPTIONS,
    id,
    zoom: undefined, // keep it
    defaultCenter: MAP_DEFAULT_CENTER,
    defaultZoom: MAP_DEFAULT_ZOOM,
    mapTypeId: mapTypeId ?? BASE_MAP_OPTIONS.mapTypeId,
    onMapTypeIdChanged,
    onClick,
  };
};

export const useMapStyle = (sizes?: MapSizes): CSSProperties => {
  const { width, height, aspectRatio } = useMapSize(sizes);

  return aspectRatio ? { width, aspectRatio } : { width, height };
};

export const useMapSize = ({
  width = "100%",
  height = "400px",
  ratio,
  windowHeightCoef,
}: MapSizes = {}) => {
  const { height: windowHeight } = useWindowDimensions();

  if (windowHeightCoef) {
    return {
      width,
      height: `${windowHeight / windowHeightCoef}px`,
    };
  }

  const ratioArray = (ratio || "").split(":");
  const ratioWidth = Number(ratioArray[0]);
  const ratioHeight = Number(ratioArray[1]);

  if (ratioWidth && ratioHeight) {
    // Use native CSS aspect-ratio if possible
    return {
      width,
      aspectRatio: `${ratioWidth} / ${ratioHeight}`,
    };
  }

  return {
    width,
    height,
  };
};
